import { motion } from "framer-motion";
import { CheckCircle2, Car, Users, Phone, Clock, MapPin, Plane, Bus } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const airportTransfer = [
  { name: "Sedan 4+1", price: "Best Price", details: "Airport Drop / Pickup", type: "Sedan" },
  { name: "SUV 6+1", price: "Best Price", details: "Airport Drop / Pickup", type: "SUV" },
  { name: "MUV 7+1", price: "Best Price", details: "Airport Drop / Pickup", type: "MUV" },
  { name: "14 Seater", price: "Best Price", details: "Airport Drop / Pickup", type: "Van" },
  { name: "17 Seater", price: "Best Price", details: "Airport Drop / Pickup", type: "Van" },
];

const dayRent = [
  { name: "Etios/Swift dzire", price: "₹3,000/-", detail: "100 KM FREE", extra: "Extra Km: ₹13" },
  { name: "Innova", price: "₹4,200/-", detail: "100 KM FREE", extra: "Extra Km: ₹15" },
  { name: "Innova Crysta", price: "₹4,800/-", detail: "100 KM FREE", extra: "Extra Km: ₹17" },
];

const outstationRates = [
  { name: "Etios/Swift dzire", price: "₹14/km", detail: "Min 300 KM Coverage", extra: "Batta: ₹500 | Hills: ₹1,500" },
  { name: "Innova", price: "₹19/km", detail: "Min 300 KM Coverage", extra: "Batta: ₹600 | Hills: ₹2,000" },
  { name: "Innova Crysta", price: "₹23/km", detail: "Min 300 KM Coverage", extra: "Batta: ₹600 | Hills: ₹2,000" },
  { name: "Tempo Traveller", price: "₹24/km", detail: "Min 300 KM Coverage", extra: "Batta: ₹800 | Hills: ₹2,500" },
];

const Pricing = () => {
  return (
    <Layout>
      <img
        src="/trichy_srirangam_taxi_hero_1776155195279.png"
        alt="trichy taxi fare taxi pricing trichy cab rates trichy to velankanni taxi price trichy madurai taxi cost outstation taxi charges trichy"
        className="hidden"
      />
      <SEOHead
        title="Taxi Pricing in Trichy | Affordable Local & Outstation Rates"
        description="trichytovelankanni.taxi - Reliable and affordable taxi services in Trichy for local sightseeing and outstation trips. Book your ride now!"
        keywords="trichy taxi fare, taxi pricing trichy, cab rates trichy, trichy to velankanni taxi price, outstation taxi charges trichy, taxi booking trichy"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 section-padding bg-gradient-to-b from-primary/10 via-background to-transparent overflow-hidden">
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-6xl font-bold tracking-tight mb-6"
            >
              Transparent <span className="text-gradient">Pricing</span> for Every Journey
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-muted-foreground"
            >
              Choose the perfect ride for your needs with our clear, all-inclusive pricing structures. No hidden fees, just honest rates.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Airport Transfer Section */}
      <section className="py-16">
        <div className="container-custom">
          <SectionHeading
            badge="Limited Offer"
            title="Airport"
            highlight="Transfers"
            description="Airport pickup / Airport drop from Trichy International Airport"
            align="left"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-12">
            {airportTransfer.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-hover rounded-3xl p-6 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Plane className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-4">{item.name}</h3>
                <p className="text-xs text-muted-foreground mb-6">{item.details}</p>
                <a
                  href={`https://wa.me/917418749974?text=Airport Transfer Booking: ${item.name}`}
                  className="w-full py-3 bg-white/5 border border-white/10 group-hover:bg-[#2b8a00] group-hover:text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  Book Now
                </a>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-white/5 rounded-2xl border border-dashed border-white/10 text-sm text-center font-medium text-gray-400">
            Note: Toll, Parking & permit charges extra.
          </div>
        </div>
      </section>

      {/* Day Rent Section */}
      <section className="py-16 bg-taxi-darker/30">
        <div className="container-custom">
          <SectionHeading
            title="Day"
            highlight="Rentals"
            description="Hourly rental trips within Trichy city limits (100 Kms Free)"
            align="right"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {dayRent.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-hover rounded-3xl p-8 transition-all duration-300 border border-white/5"
              >
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6">
                  <Clock className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">{item.name}</h3>
                <p className="text-3xl font-black text-blue-600 mb-2">{item.price}</p>
                <p className="text-sm text-foreground font-bold mb-2">{item.detail}</p>
                <p className="text-xs text-muted-foreground mb-8">{item.extra}</p>
                <a
                  href={`https://wa.me/917418749974?text=Day Rent Booking: ${item.name}`}
                  className="w-full py-4 bg-primary text-primary-foreground rounded-2xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                >
                  Book Now
                </a>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-background/50 rounded-2xl border border-dashed border-white/10 text-sm text-center font-medium text-gray-400">
            Note: Toll, Parking & permit charges extra.
          </div>
        </div>
      </section>

      {/* Velankanni Special Highlight */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="container-custom">
          <motion.div 
            {...fadeUp}
            className="bg-taxi-darker border-2 border-primary/30 rounded-[3rem] p-8 sm:p-12 relative overflow-hidden group shadow-2xl"
          >
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-8 py-2 rounded-bl-[2rem] font-black text-sm tracking-widest animate-pulse">
              MOST POPULAR ROUTE
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-5xl font-black text-white mb-6">
                  Trichy to <span className="text-gradient">Velankanni</span> Special
                </h2>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed font-medium">
                  Our most requested pilgrimage route. Experience a divine journey with specialized drivers, comfortable vehicles, and timely service. 
                </p>
                <div className="grid grid-cols-2 gap-4 mb-10">
                  <div className="bg-white/5 p-4 rounded-2xl border border-white/10">
                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Sedan</p>
                    <p className="text-2xl font-black text-white">₹4,840</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-2xl border border-white/10">
                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Innova</p>
                    <p className="text-2xl font-black text-white">₹6,390</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <a href="https://wa.me/917418749974?text=Velankanni Special Booking" className="btn-primary px-8 py-4 rounded-2xl font-bold shadow-xl">
                    Book Special Package
                  </a>
                  <div className="flex items-center gap-2 text-primary font-bold">
                    <CheckCircle2 className="w-5 h-5" /> 155 Kms Covered
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" />
                <img 
                  src="/velankanni_hero.png" 
                  alt="Trichy to Velankanni Taxi Service" 
                  className="relative z-10 rounded-3xl shadow-2xl border border-white/10 transform group-hover:scale-[1.02] transition-transform duration-500" 
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Outstation Section */}
      <section className="py-16">
        <div className="container-custom">
          <SectionHeading
            title="Outstation"
            highlight="Rates"
            description="One-way and Round trip rates for outstation travel (Min 300km coverage)"
            align="left"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {outstationRates.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-hover rounded-3xl p-6 transition-all duration-300 border-t-4 border-t-orange-500"
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold mb-1">{item.name}</h3>
                <p className="text-2xl font-black text-orange-600 mb-1">{item.price}</p>
                <p className="text-sm text-muted-foreground font-bold mb-1">{item.detail}</p>
                <p className="text-[10px] text-muted-foreground mb-6">{item.extra}</p>
                <a
                  href={`https://wa.me/917418749974?text=Outstation Booking: ${item.name}`}
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  Book Now
                </a>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-white/5 rounded-2xl border border-dashed border-white/10 text-sm text-center font-medium text-gray-400">
            Note: Toll, Parking & permit charges extra. Hill station charges extra as applicable.
          </div>
        </div>
      </section>

      {/* Note Section (Simplified) */}
      <section className="pb-24">
        <div className="container-custom max-w-4xl pt-10">
          <motion.div
            {...fadeUp}
            className="bg-taxi-darker border border-white/10 rounded-[2.5rem] p-8 sm:p-12 shadow-2xl relative overflow-hidden text-center"
          >
            <h2 className="text-2xl font-bold mb-6">Need a <span className="text-primary">Custom Quote?</span></h2>
            <p className="text-muted-foreground mb-10">Waiting for a special itinerary or group booking? We offer customized packages tailored to your specific travel needs.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+917418749974" className="bg-[#2b8a00] hover:bg-[#1e6000] text-white inline-flex items-center gap-2 py-4 px-8 rounded-2xl font-bold shadow-lg transition-colors">
                <Phone className="w-5 h-5" /> Call Now
              </a>
              <a href="https://wa.me/917418749974" className="inline-flex items-center gap-2 py-4 px-8 rounded-2xl font-bold border-2 border-[#2b8a00] text-[#2b8a00] hover:bg-[#2b8a00]/5 transition-colors">
                <Phone className="w-5 h-5 opacity-0 absolute" /> {/* Hidden icon for space */}
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Pricing;
